/**
 * Fall Scale API client.
 *
 * createFallScaleApi({ baseUrl, getAuthHeaders }) -> { getConfig, listAssessments, getAssessment, createAssessment }
 *
 *  - baseUrl        : API root. Default "/api/fall-scale" (same-origin / dev proxy).
 *  - getAuthHeaders : optional () => ({ Authorization: "Bearer ..." }) for your JWT.
 *
 * Every method throws an Error with `.status` and `.details` (string[]) on failure,
 * so the UI can surface the backend's validation messages.
 */

import { baseUrl } from "../../../../../../http-common"; // <-- Change this path

export function createFallScaleApi({
  baseUrl: apiBaseUrl = `${baseUrl}/fall-scale`,
  getAuthHeaders,
} = {}) {

  async function request(path, init = {}) {
    const headers = {
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...(getAuthHeaders ? getAuthHeaders() : {}),
      ...(init.headers || {}),
    };

    const res = await fetch(`${apiBaseUrl}${path}`, {
      ...init,
      headers,
    });

    if (!res.ok) {
      let body = null;
      try {
        body = await res.json();
      } catch { }

      const err = new Error(body?.message || `Request failed (${res.status})`);
      err.status = res.status;
      err.details = body?.details || [];
      throw err;
    }

    return res.status === 204 ? null : res.json();
  }

  return {
    getConfig: () => request("/config"),

    listAssessments: (mrn) =>
      request(`/assessments${mrn ? `?mrn=${encodeURIComponent(mrn)}` : ""}`),

    getAssessment: (id) =>
      request(`/assessments/${id}`),

    createAssessment: (payload) =>
      request("/assessments", {
        method: "POST",
        body: JSON.stringify(payload),
      }),

    getAssessmentPdf: async (id) => {
      const res = await fetch(`${apiBaseUrl}/assessments/${id}/pdf`, {
        headers: {
          ...(getAuthHeaders ? getAuthHeaders() : {}),
        },
      });

      if (!res.ok) {
        const err = new Error(`PDF request failed (${res.status})`);
        err.status = res.status;
        throw err;
      }

      return res.blob();
    },
  };
}